import { useState, useEffect } from 'react';
import { useAuth } from '@context';
import { useAuthHttp } from '@hooks';
import { GET_NOVU_CONFIG } from '@constants';
import { retryDelayForAttempt } from './retryBackoff';

export interface NovuConfig {
  applicationIdentifier: string;
}

interface CachedNovuConfig {
  config: NovuConfig;
  timestamp: number;
}

const NOVU_CONFIG_STORAGE_KEY = 'novuConfig';
const CONFIG_CACHE_DURATION_MS = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds

/**
 * Read the cached config, reporting whether it is past its 3 day refresh window rather than
 * discarding it. An expired identifier still initialises the SDK, so it is the fallback when
 * the config endpoint is unreachable.
 */
const readCachedConfig = (): {
  config: NovuConfig;
  isStale: boolean;
} | null => {
  try {
    const cached = localStorage.getItem(NOVU_CONFIG_STORAGE_KEY);
    if (!cached) return null;

    const { config, timestamp }: CachedNovuConfig = JSON.parse(cached);
    if (!config?.applicationIdentifier) {
      localStorage.removeItem(NOVU_CONFIG_STORAGE_KEY);
      return null;
    }

    return {
      config,
      isStale: Date.now() - timestamp > CONFIG_CACHE_DURATION_MS,
    };
  } catch (err) {
    console.error('Failed to read cached Novu config:', err);
    localStorage.removeItem(NOVU_CONFIG_STORAGE_KEY);
    return null;
  }
};

/**
 * Store config in localStorage with current timestamp
 */
const setCachedConfig = (config: NovuConfig): void => {
  try {
    const cachedData: CachedNovuConfig = {
      config,
      timestamp: Date.now(),
    };
    localStorage.setItem(NOVU_CONFIG_STORAGE_KEY, JSON.stringify(cachedData));
  } catch (err) {
    console.error('Failed to cache Novu config:', err);
  }
};

export const useNovuConfig = () => {
  const [config, setConfig] = useState<NovuConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { isAuthenticated } = useAuth();
  const authHttp = useAuthHttp();

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return undefined;
    }

    let isEffectActive = true;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let failedAttempts = 0;

    const loadConfig = async () => {
      if (!isEffectActive) return;

      const cached = readCachedConfig();

      if (cached && !cached.isStale) {
        setConfig(cached.config);
        setError(null);
        setLoading(false);
        return;
      }

      try {
        const { data } = await authHttp.get<NovuConfig>(GET_NOVU_CONFIG);
        if (!isEffectActive) return;

        if (!data?.applicationIdentifier) {
          throw new Error('Novu config response had no applicationIdentifier');
        }

        setCachedConfig(data);
        setConfig(data);
        setError(null);
        failedAttempts = 0;
      } catch (err) {
        if (!isEffectActive) return;

        setError(err as Error);
        console.error('Failed to fetch Novu config:', err);

        if (cached) {
          setConfig(cached.config);
        }

        retryTimeout = setTimeout(
          loadConfig,
          retryDelayForAttempt(failedAttempts)
        );
        failedAttempts += 1;
      } finally {
        if (isEffectActive) setLoading(false);
      }
    };

    loadConfig();

    return () => {
      isEffectActive = false;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return { config, loading, error };
};
